/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  InputNumber,
  InputNumberProps,
  message,
} from 'antd';
import { Formik, FormikHelpers } from 'formik';
import React, { memo, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dictionaries } from '../../interfaces';
import { GeneralApi, OptionApi, ValuationApi } from '../../apis';
import * as Yup from 'yup';
import { QuerySearch } from '../form/query-search';
import { UploadImage } from '../upload';
import { CYR_NAME, REG_NO } from './custom';

export const formSchema = (values: any) =>
  Yup.object().shape({
    vin: Yup.string()
      .matches(
        /^[A-HJ-NPR-Z0-9]{10,17}$/,
        'VIN зөвхөн латин ТОМ үсэг (I, O, Q үсэггүй) ба 0–9 тоо байх ёстой.'
      )
      .required('VIN-ийг заавал оруулна уу.'),
    price: Yup.number().min(1, '0 -с их байх шаардаглгатай').required('Заавал оруулах'),
    modelId: Yup.string().required('Заавал оруулах'),
    imageUrls: Yup.array().min(4, '4 ширх зураг').required('Заавал оруулах'),
    fullName: Yup.string()
      .trim()
      .matches(CYR_NAME, 'Зөвхөн крилл үсэг (зай, зураас) ашиглана уу')
      .required('Овог нэр заавал оруулах'),
    registerNo: Yup.string()
      .transform((v) => (v ? v.toUpperCase().replace(/\s/g, '') : v))
      .matches(REG_NO, 'Формат: 2 крилл үсэг + 8 тоо (ж: УБ12345678)')
      .required('Регистрийн дугаар заавал оруулах'),
  });

type FormDataType = {
  vin?: string;
  valuateType: string;
  price?: number;
  modelId?: string;
  optionId?: string;
  fullName?: string;
  registerNo?: string;
  imageUrls?: string[];
};

type Props = {
  setAction: React.Dispatch<
    React.SetStateAction<{
      loading: boolean;
      payload: undefined;
    }>
  >;
};

const ValuationVinForm = memo(({ setAction }: Props) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [general, setGeneral] = useState<Dictionaries>();
  const loadData = useCallback(async () => {
    const res = await GeneralApi.init();
    setGeneral(res);
  }, []);
  const [loading, setLoading] = useState(false);

  const initialValues: FormDataType = {
    valuateType: 'VIN',
    vin: undefined,
    price: undefined,
    modelId: undefined,
    optionId: undefined,
    fullName: undefined,
    registerNo: undefined,
    imageUrls: [],
  };

  const onSubmit = useCallback(
    async (values: FormDataType) => {
      setAction({
        loading: true,
        payload: undefined,
      });

      try {
        setLoading(true);
        console.log('on submit :', values);
        const res = await ValuationApi.valuate(values);

        setAction({
          loading: false,
          payload: res,
        });
        setLoading(false);
      } catch (err: any) {
        messageApi.error(err.message);
        setLoading(false);
        setAction({
          loading: false,
          payload: undefined,
        });
      }
    },
    [messageApi, setAction]
  );

  const getOption = useCallback((value?: { code?: string; name: string; id: string }[] | any) => {
    if (!value) {
      return [];
    }

    return value.map((item: any) => ({
      label: item.name,
      value: item.code ?? item.id,
    }));
  }, []);

  const formatter: InputNumberProps<number>['formatter'] = (value) => {
    const [start, end] = `${value}`.split('.') || [];
    const v = `${start}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `₮ ${end ? `${v}.${end}` : `${v}`}`;
  };

  const parseNumber = (value?: string) => {
    if (!value) return 0;
    const cleaned = value.replace(/[^\d.-]/g, ''); 
    const n = parseFloat(cleaned);
    return Number.isNaN(n) ? 0 : n;
  };

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <>
      {contextHolder}
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={(values: FormDataType, actions: FormikHelpers<FormDataType>) => onSubmit(values)}
      >
        {({ values, setFieldValue, handleSubmit, errors, touched }) => {
          console.log('modelId :', values.modelId);
          return (
            <Container>
              <Form
                layout="vertical"
                className="form sm:w-full md:w-full lg:w-[600px] xl:w-[500px] 2xl-[500px] w-[500px]"
                onFinish={handleSubmit}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Овог, нэр"
                      validateStatus={errors.fullName && touched.fullName ? 'error' : ''}
                      help={errors.fullName && touched.fullName ? errors.fullName : ''}
                    >
                      <Input
                        name="fullName"
                        status={errors.fullName && touched.fullName ? 'error' : ''}
                        value={values.fullName}
                        onChange={(e) => setFieldValue('fullName', e.target.value)}
                        placeholder="Зээл хүсэгчийн овог, нэр"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Регистрийн дугаар"
                      validateStatus={errors.registerNo && touched.registerNo ? 'error' : ''}
                      help={errors.registerNo && touched.registerNo ? errors.registerNo : ''}
                    >
                      <Input
                        name="registerNo"
                        value={(values as any).registerNo}
                        onChange={(e) => setFieldValue('registerNo', e.target.value)}
                        placeholder="Регистрийн дугаар"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Загвар"
                      validateStatus={errors.modelId && touched.modelId ? 'error' : ''}
                      help={errors.modelId && touched.modelId ? errors.modelId : ''}
                    >
                      <Select
                        placeholder="Загвар сонгох"
                        options={getOption(general?.carModels || [])}
                        value={values.modelId}
                        onChange={(val) => setFieldValue('modelId', val)}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? ('' as any)).toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      label="Тоноглол"
                      layout="vertical"
                      validateStatus={errors.optionId && touched.optionId ? 'error' : ''}
                      help={errors.optionId && touched.optionId ? errors.optionId : ('' as any)}
                    >
                      <QuerySearch
                        value={values.optionId!}
                        onChange={(e) => setFieldValue('optionId', e)}
                        placeholder="Тоноглол"
                        showSearch
                        loadData={OptionApi.select(values.modelId!) as any}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Арлын дугаар"
                      validateStatus={errors.vin && touched.vin ? 'error' : ''}
                      help={errors.vin && touched.vin ? errors.vin : ''}
                    >
                      <Input
                        name="vin"
                        status={errors.vin && touched.vin ? 'error' : ''}
                        value={values.vin}
                        onChange={(e) => setFieldValue('vin', e.target.value)}
                        placeholder="Арлын дугаар"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Үнэ"
                      validateStatus={errors.price && touched.price ? 'error' : ''}
                      help={errors.price && touched.price ? errors.price : ''}
                    >
                      <InputNumber
                        name="price"
                        status={errors.price && touched.price ? 'error' : ''}
                        value={values.price}
                        onChange={(e) => setFieldValue('price', e)}
                        placeholder="Үнэ"
                        formatter={formatter}
                        className="w-full"
                        parser={parseNumber}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="flex flex-row gap-3 mb-3">
                  <UploadImage
                    error={errors.imageUrls}
                    callBack={(e) => setFieldValue('imageUrls', [...(values.imageUrls || []), e])}
                  />
                  <UploadImage
                    error={errors.imageUrls}
                    callBack={(e) => setFieldValue('imageUrls', [...(values.imageUrls || []), e])}
                  />
                  <UploadImage
                    error={errors.imageUrls}
                    callBack={(e) => setFieldValue('imageUrls', [...(values.imageUrls || []), e])}
                  />
                  <UploadImage
                    error={errors.imageUrls}
                    callBack={(e) => setFieldValue('imageUrls', [...(values.imageUrls || []), e])}
                  />
                </div>
                {errors.imageUrls && (
                  <div>
                    <div className="text-red-500">Та зургаа оруулна уу</div>
                    <div className="h-[10px]" />
                  </div>
                )}

                <Button
                  className="primary-btn"
                  type="primary"
                  onClick={() => handleSubmit()}
                  loading={loading}
                >
                  Үнэлгээ хийх
                </Button>
              </Form>
            </Container>
          );
        }}
      </Formik>
    </>
  );
});

const Container = styled.div`
  border: solid 1px #ffefee;
  border-radius: 8px;
  padding: 16px;
  width: '100%';

  .form {
    /* width: 100%; */
  }
`;

export const parseNumber = (value?: string) => {
  if (!value) return 0;
  const cleaned = value.replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? 0 : n;
};

ValuationVinForm.displayName = 'ValuationVinForm';

export { ValuationVinForm };
